/**
 * What a sprite that depicts a BODY promises (D236) — the D89 promise,
 * actor-side, judged per binding room.
 *
 * @remarks
 * An item sprite promises the thing referable where it is drawn, and the
 * loader holds that statically: the item is placed in the room, or ambient.
 * A body has no such fact to point at. Where it stands is state — it arrives
 * with the gas, it falls at the grate, it is moved offstage — so the only
 * honest place for "the body is here" is the sprite's own `visibleWhen`,
 * and the rule is that the condition SAYS it:
 *
 * - **The body is held to the room.** Whenever `visibleWhen` holds, the
 *   actor must be present where the sprite is drawn: the condition requires
 *   `actor_present` for it, or `actor_in_room` for it and this room. A
 *   sprite gated on a flag alone draws the animal wherever the flag is true,
 *   including after a `move_actor` took it away.
 * - **Standing is not being seen.** An actor that declares its own
 *   `visibleWhen` is not in the catalog until that holds, so `actor_in_room`
 *   would draw a body the player cannot name. Such a sprite gates on
 *   `actor_present`, which reads both.
 * - **The room is one the body can reach.** An `actor_present` sprite in a
 *   room no `initialRoomId` or `move_actor` puts the actor in can never
 *   show. (An `actor_in_room` naming such a room is the producibility walk's
 *   to refuse, and it does — said once, there.) This is also what keeps a
 *   body heard and never seen, the occupant behind the steel door, out of
 *   every picture: she can stand nowhere.
 *
 * "Requires" is implication, read structurally: a leaf requires itself;
 * `all_of` requires what any member requires; `any_of` what every member
 * does; `at_least_of` (k of n) what at least n − k + 1 members do, because
 * any k that hold include one of them. `not` requires nothing. Sound and
 * deliberately not complete — a flag the author knows tracks the body is
 * not a fact the loader can see, and the fix is to say the fact.
 *
 * @packageDocumentation
 */

import type { ActorDefinition, ActorId, ConditionDefinition, RoomId, SpriteDefinition } from '@orpheus/content-schema';

import { brokenReference } from './broken-reference.js';
import { type ReferenceBrokenFailure } from './load-failure.js';

/** Nothing about the body follows from the condition. */
const NOT_HELD = 0;
/** The body stands in the room (`actor_in_room`) — whether or not it can be seen there. */
const STANDS_HERE = 1;
/** The body is present (`actor_present`): standing where the player is, and visible. */
const PRESENT_HERE = 2;

/** How strongly a condition holds one body to one room, weakest first. */
type BodyHold = typeof NOT_HELD | typeof PRESENT_HERE | typeof STANDS_HERE;

/**
 * The strongest hold at least `mustHoldCount` of the members guarantee —
 * one rule for the three combinators (all-of needs one member, any-of every
 * member, at-least-of k-of-n needs n − k + 1).
 *
 * @param memberHolds - Each member's own hold
 * @param mustHoldCount - How many members must carry a hold for the combinator to
 * @returns The hold the combinator guarantees
 */
const holdCarriedBy = (memberHolds: readonly BodyHold[], mustHoldCount: number): BodyHold =>
	([PRESENT_HERE, STANDS_HERE] as const).find((level) => memberHolds.filter((hold) => hold >= level).length >= mustHoldCount) ?? NOT_HELD;

/**
 * How strongly a condition, whenever it holds, puts the actor in the room.
 *
 * @param condition - The sprite's `visibleWhen`, or a part of it
 * @param actorId - The depicted actor
 * @param roomId - The room the sprite is bound in
 * @returns The hold — see {@link BodyHold}
 */
const bodyHoldOf = (condition: ConditionDefinition, actorId: ActorId, roomId: RoomId): BodyHold => {
	const holdsOf = (members: readonly ConditionDefinition[]): readonly BodyHold[] => members.map((member) => bodyHoldOf(member, actorId, roomId));

	switch (condition.type) {
		case 'actor_in_room':
			return condition.actorId === actorId && condition.roomId === roomId ? STANDS_HERE : NOT_HELD;
		case 'actor_present':
			return condition.actorId === actorId ? PRESENT_HERE : NOT_HELD;
		case 'all_of':
			return holdCarriedBy(holdsOf(condition.conditions), 1);
		case 'any_of':
			return holdCarriedBy(holdsOf(condition.conditions), condition.conditions.length);
		case 'at_least_of':
			return holdCarriedBy(holdsOf(condition.conditions), condition.conditions.length - condition.minimumCount + 1);
		default:
			return NOT_HELD;
	}
};

/**
 * Judge one binding of a sprite that depicts an actor — see the module remarks.
 *
 * @param sprite - The sprite, whose `depicts` names `actor`
 * @param actor - The depicted actor
 * @param binding - Where the sprite is bound: the room, the rooms the actor can stand in, and the binding's path
 * @param binding.bindingPath - The binding's locator (`rooms[11].sprites[0]`)
 * @param binding.roomId - The room binding the sprite
 * @param binding.standableRoomIds - Every room the actor can ever stand in
 * @returns The failures (empty = the picture keeps its promise in this room)
 */
export const bodyDepictionFailures = (
	sprite: SpriteDefinition,
	actor: ActorDefinition,
	binding: { readonly bindingPath: string; readonly roomId: RoomId; readonly standableRoomIds: readonly RoomId[] },
): readonly ReferenceBrokenFailure[] => {
	const { bindingPath, roomId, standableRoomIds } = binding;
	const hold = bodyHoldOf(sprite.visibleWhen, actor.actorId, roomId);

	if (hold === NOT_HELD) {
		return [
			brokenReference(
				bindingPath,
				actor.actorId,
				`the sprite "${sprite.spriteId}" depicts "${actor.actorId}", but its visibleWhen does not hold the body here — require actor_present, or actor_in_room for "${roomId}": a composite must not show a body where the body is not`,
			),
		];
	}

	if (hold === STANDS_HERE && actor.visibleWhen !== undefined) {
		return [
			brokenReference(
				bindingPath,
				actor.actorId,
				`the sprite "${sprite.spriteId}" depicts "${actor.actorId}", which declares its own visibleWhen — standing in "${roomId}" is not being seen there, so gate the sprite on actor_present, which reads both`,
			),
		];
	}

	if (hold === PRESENT_HERE && !standableRoomIds.includes(roomId)) {
		return [
			brokenReference(
				bindingPath,
				actor.actorId,
				`the sprite "${sprite.spriteId}" depicts "${actor.actorId}" in "${roomId}", where no initialRoomId or move_actor ever puts it — the sprite can never show`,
			),
		];
	}

	return [];
};
